import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Footprints, Send, FileText } from "lucide-react";
import { ujmsServiceQueue, ujmsServiceAttempt, ujmsOpenDocument } from "../../../services/ujmsApi";
import { Badge, cls, fmtD, fmtDT, Stat, Seg, Field, btnGhost, btnPrimary, inputCls } from "../../ui";
import { SectionHdr } from "../SummaryTab";
import { DataGrid } from "../DataGrid";
import { ServiceReturnDialog } from "../CaseEditDialogs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";

const VIEWS = [["", "Pending"], ["SERVED", "Served"], ["RETURNED_UNSERVED", "Unserved"]];

/** Sheriff Civil Process desk — every Summons the Clerk routed electronically, with attempt + return-of-service tracking. */
export function CivilProcessQueue() {
  const [data, setData] = useState(null);
  const [view, setView] = useState("");
  const [attempt, setAttempt] = useState(null);
  const [ret, setRet] = useState(null);
  const load = useCallback(() => ujmsServiceQueue(view ? { status: view } : {}).then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Service queue unavailable")), [view]);
  useEffect(() => { load(); }, [load]);
  const k = data?.counts || {};
  const cols = [
    { key: "form_number", label: "Summons #", width: 130, render: (r) => <span className="font-mono font-semibold text-ujms-dark">{r.form_number}</span> },
    { key: "case_number", label: "Case", width: 120, render: (r) => <Link to={`/cases/${r.case_id}?tab=service`} className="font-mono font-semibold text-ujms-dark underline" onClick={(e) => e.stopPropagation()} data-testid={`svc-case-${r.id}`}>{r.case_number}</Link> },
    { key: "caption", label: "Caption", width: 240, text: (r) => r.caption || "—" },
    { key: "party_name", label: "Serve on", width: 180 },
    { key: "party_address", label: "Address", width: 200, text: (r) => r.party_address || "— no address on file —" },
    { key: "issued_date", label: "Issued", width: 90, text: (r) => fmtD(r.issued_date) },
    { key: "expires_at", label: "Expires", width: 110, render: (r) => <span className={cls("font-mono", r.expired && "text-red-700 font-bold")}>{fmtD(r.expires_at)}{r.expired && " EXPIRED"}</span> },
    { key: "return_date", label: "Return date", width: 100, text: (r) => fmtD(r.return_date) },
    { key: "attempts", label: "Attempts", width: 80, text: (r) => String((r.attempts || []).length) },
    { key: "status", label: "Status", width: 150, render: (r) => <Badge value={r.status} /> },
    { key: "_a", label: "", sortable: false, width: 230, render: (r) => ["ISSUED", "ATTEMPTED"].includes(r.status) ? (
      <span className="inline-flex gap-1">
        <button className={btnGhost} onClick={(e) => { e.stopPropagation(); setAttempt(r); }} data-testid={`svc-attempt-${r.id}`}><Footprints className="w-3 h-3" /> Attempt</button>
        <button className={btnPrimary} onClick={(e) => { e.stopPropagation(); setRet(r); }} data-testid={`svc-return-${r.id}`}><Send className="w-3 h-3" /> Return</button>
        {r.document_id && <button className={btnGhost} onClick={(e) => { e.stopPropagation(); ujmsOpenDocument(r.document_id, r.form_number); }} data-testid={`svc-pdf-${r.id}`}><FileText className="w-3 h-3" /></button>}
      </span>) : (r.returned_at ? <span className="text-[10px] text-slate-500">{fmtDT(r.returned_at)} · {r.returned_by}</span> : null) },
  ];
  return (
    <div className="space-y-1.5" data-testid="civil-process-queue">
      <div className="grid grid-cols-3 gap-1" data-testid="svc-kpis">
        <Stat label="Pending service" value={k.pending} tone="text-amber-700" testId="svc-kpi-pending" />
        <Stat label="Served (30 days)" value={k.served_30d} tone="text-emerald-700" testId="svc-kpi-served" />
        <Stat label="Returned unserved (30 days)" value={k.unserved_30d} tone="text-red-700" testId="svc-kpi-unserved" />
      </div>
      <section className="ent-panel">
        <SectionHdr title="Civil process — Summons routed electronically from the Clerk" right={<span className="inline-flex items-center gap-2"><Seg options={VIEWS} value={view} onChange={setView} testId="svc-view" /><button className="underline" onClick={load} data-testid="svc-refresh"><RefreshCw className="w-3 h-3 inline" /></button></span>} />
        <div className="p-1">
          <DataGrid id="sheriff-service" columns={cols} rows={data?.rows || []} loading={!data} pdfTitle="Sheriff civil process queue" emptyText="Nothing awaiting service — Summons appear here the moment the Clerk accepts a civil complaint or issues one by hand." />
        </div>
      </section>
      <AttemptDialog rec={attempt} onClose={() => setAttempt(null)} onDone={() => { setAttempt(null); load(); }} />
      <ServiceReturnDialog record={ret} onClose={() => setRet(null)} onDone={() => { setRet(null); load(); }} />
    </div>
  );
}

function AttemptDialog({ rec, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNotes(""); }, [rec]);
  if (!rec) return null;
  const go = async () => {
    setBusy(true);
    try { await ujmsServiceAttempt(rec.id, { notes }); toast.success(`Attempt recorded on ${rec.form_number}`); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not record the attempt"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="svc-attempt-dialog">
        <DialogHeader><DialogTitle>Service attempt — {rec.form_number} on {rec.party_name}</DialogTitle></DialogHeader>
        <div className="text-[11px] text-slate-600">{rec.case_number} · {rec.caption}. Previous attempts: {(rec.attempts || []).length}.</div>
        {(rec.attempts || []).map((a, i) => <div key={i} className="text-[11px] border-l-2 border-slate-300 pl-2 text-slate-600">{fmtDT(a.at)} · {a.by} — {a.notes}</div>)}
        <Field label="What happened (address, time, result)" required><input className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. 2:10 pm — no answer at 123 W Main, card left" data-testid="svc-attempt-notes" /></Field>
        <DialogFooter><button className={btnGhost} onClick={onClose}>Cancel</button><button className={btnPrimary} disabled={busy || notes.trim().length < 5} onClick={go} data-testid="svc-attempt-submit">Record attempt</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
